import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { FlitshipToaster } from "./components/ui/flitship-toast";
import { AppLayout, PublicLayout } from "./components/AppLayout";
import { CoachMarkProvider } from "./components/CoachMarkSystem";
import CookieBanner from "./components/CookieBanner";

// ============ LAZY LOADED: LEGAL PAGES ============
const LegalLayout = lazy(() => import("./pages/legal/LegalLayout"));
const CGU = lazy(() => import("./pages/legal/CGU"));
const Confidentialite = lazy(() => import("./pages/legal/Confidentialite"));
const TrustProtocol = lazy(() => import("./pages/legal/TrustProtocol"));
const CookiesPage = lazy(() => import("./pages/legal/Cookies"));
const MentionsLegales = lazy(() => import("./pages/legal/MentionsLegales"));

// ============ LAZY LOADED: PUBLIC PAGES ============
const Homepage = lazy(() => import("./pages/Homepage"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const EmailConfirmed = lazy(() => import("./pages/EmailConfirmed"));

// ============ LAZY LOADED: APP PAGES ============
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ColisHub = lazy(() => import("./pages/ColisHub"));
const TrajetsHub = lazy(() => import("./pages/TrajetsHub"));
const Requests = lazy(() => import("./pages/Requests"));
const RequestDetail = lazy(() => import("./pages/RequestDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Notifications = lazy(() => import("./pages/Notifications"));

// ============ LAZY LOADED: COLIS FLOW PAGES ============
const SendPackage = lazy(() => import("./pages/SendPackage"));
const CreateShipment = lazy(() => import("./pages/CreateShipment"));
const MyShipments = lazy(() => import("./pages/MyShipments"));
const SenderShipmentDetail = lazy(() => import("./pages/SenderShipmentDetail"));

// ============ LAZY LOADED: TRAJETS FLOW PAGES ============
const PublishTrip = lazy(() => import("./pages/PublishTrip"));
const PublishTripFlow = lazy(() => import("./pages/PublishTripFlow"));
const MyTrip = lazy(() => import("./pages/MyTrip"));
const CompatibleShipments = lazy(() => import("./pages/CompatibleShipments"));
const ShipmentDetail = lazy(() => import("./pages/ShipmentDetail"));

// ============ LAZY LOADED: MISSION PAGES ============
const Missions = lazy(() => import("./pages/Missions"));
const Mission = lazy(() => import("./pages/Mission"));
const MissionConfirmation = lazy(() => import("./pages/MissionConfirmation"));

// ============ LAZY LOADED: PROFILE/IDENTITY PAGES ============
const IdentityVerification = lazy(() => import("./pages/IdentityVerification"));
const VerificationStatus = lazy(() => import("./pages/VerificationStatus"));

// ============ LAZY LOADED: OTHER PAGES ============
const Explore = lazy(() => import("./pages/Explore"));
const Publish = lazy(() => import("./pages/Publish"));
const Messages = lazy(() => import("./pages/Messages"));
const Admin = lazy(() => import("./pages/Admin"));
const Diagnostic = lazy(() => import("./pages/Diagnostic"));
const ItemCheck = lazy(() => import("./pages/ItemCheck"));
const TrustScoreShowcase = lazy(() => import("./pages/TrustScoreShowcase"));
const HeroStatusPage = lazy(() => import("./pages/HeroStatusPage"));

const ONESIGNAL_APP_ID = "4c147512-e2e9-46a0-9acf-d8b909375bb9";

const initOneSignal = async () => {
  try {
    if (typeof window !== 'undefined' && window.OneSignalDeferred) {
      window.OneSignalDeferred.push(async function(OneSignal) {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          safari_web_id: "web.onesignal.auto.5dcf04a7-d9b5-4793-8717-b5ec1870e3bb",
          notifyButton: {
            enable: false,
          },
          allowLocalhostAsSecureOrigin: true,
          promptOptions: {
            slidedown: {
              prompts: [
                {
                  type: "push",
                  autoPrompt: false,
                  text: {
                    actionMessage: "Souhaitez-vous recevoir des notifications pour suivre vos colis et missions ?",
                    acceptButton: "Autoriser",
                    cancelButton: "Plus tard",
                  }
                }
              ]
            }
          }
        });
      });
    }
  } catch (error) {
    // Silent error handling
  }
};

export const requestPushPermission = async (userEmail, userId) => {
  try {
    if (typeof window !== 'undefined' && window.OneSignalDeferred) {
      window.OneSignalDeferred.push(async function(OneSignal) {
        try {
          // Login user with their ID
          await OneSignal.login(userId);
          
          // Add email if provided
          if (userEmail) {
            await OneSignal.User.addEmail(userEmail);
          }
          
          // Check if already permitted
          const permission = await OneSignal.Notifications.permission;
          if (!permission) {
            await OneSignal.Slidedown.promptPush();
          }
          
          // Save player_id to backend after a delay
          setTimeout(async () => {
            try {
              const pid = OneSignal.User.PushSubscription.id;
              if (pid) {
                const token = localStorage.getItem('sb-access-token') ||
                  sessionStorage.getItem('sb-access-token');
                if (token) {
                  await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/push/register`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({ player_id: pid })
                    }
                  );
                }
              }
            } catch (e) { /* silent */ }
          }, 2000);
        } catch (e) {
          // Silent error handling
        }
      });
    }
  } catch (error) {
    // Silent error handling
  }
};

export const getOneSignalPlayerId = () => {
  try {
    if (typeof window !== 'undefined' && window.OneSignal && window.OneSignal.User && window.OneSignal.User.PushSubscription) {
      return window.OneSignal.User.PushSubscription.id;
    }
    return null;
  } catch (error) {
    // Silent error handling
    return null;
  }
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#faf6f2'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #3c6df0',
      borderTop: '3px solid transparent',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * Flitship App Router
 * 
 * Architecture:
 * - PUBLIC routes: /, /login, /signup (no navigation)
 * - APP routes: /app/* (with bottom navigation, requires auth)
 * - ADMIN routes: /admin (special access)
 * 
 * Route mapping:
 * - /app → Dashboard (Accueil)
 * - /colis → ColisHub (Sender central)
 * - /trajets → TrajetsHub (Traveler central)
 * - /requests → Requests (Decision hub)
 * - /profile → Profile (Identity & settings)
 */
function App() {
  useEffect(() => {
    initOneSignal();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <CoachMarkProvider>
          <BrowserRouter>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* ============ PUBLIC ROUTES ============ */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Homepage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/email-confirmed" element={<EmailConfirmed />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                
                {/* Diagnostic flow (public) */}
                <Route path="/diagnostic/*" element={<Diagnostic />} />

                {/* Item Check (public) */}
                <Route path="/item-check" element={<ItemCheck />} />
              </Route>

              {/* ============ LEGAL ROUTES ============ */}
              <Route path="/legal" element={<LegalLayout />}>
                <Route index element={<CGU />} />
                <Route path="cgu" element={<CGU />} />
                <Route path="confidentialite" element={<Confidentialite />} />
                <Route path="trust-protocol" element={<TrustProtocol />} />
                <Route path="cookies" element={<CookiesPage />} />
                <Route path="mentions" element={<MentionsLegales />} />
              </Route>
              
              {/* Standalone legal routes for direct access */}
              <Route path="/cgu" element={<Navigate to="/legal/cgu" replace />} />
              <Route path="/mentions-legales" element={<Navigate to="/legal/mentions" replace />} />

              {/* ============ PUBLIC PROFILE ROUTE (No Auth Required) ============ */}
              <Route path="/p/:userId" element={<PublicProfile />} />

              {/* ============ APP ROUTES (Authenticated) ============ */}
              <Route element={<AppLayout />}>
                {/* Main tabs */}
                <Route path="/app" element={<Dashboard />} />
                <Route path="/dashboard" element={<Navigate to="/app" replace />} />
                <Route path="/colis" element={<ColisHub />} />
                <Route path="/trajets" element={<TrajetsHub />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/requests/:requestId" element={<RequestDetail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/notifications" element={<Notifications />} />

                {/* Colis flow pages */}
                <Route path="/send-package" element={<SendPackage />} />
                <Route path="/create-shipment" element={<CreateShipment />} />
                <Route path="/my-shipments" element={<MyShipments />} />
                <Route path="/my-shipments/:shipmentId" element={<SenderShipmentDetail />} />

                {/* Trajets flow pages */}
                <Route path="/publish-trip/*" element={<PublishTripFlow />} />
                {/* Legacy route for old publish trip form */}
                <Route path="/publish-trip-old" element={<Navigate to="/publish-trip" replace />} />
                <Route path="/my-trip" element={<MyTrip />} />
                <Route path="/parcels" element={<CompatibleShipments />} />
                <Route path="/parcels/:shipmentId" element={<ShipmentDetail />} />
                <Route path="/publish" element={<Publish />} />

                {/* Mission pages */}
                <Route path="/missions" element={<Missions />} />
                <Route path="/mission/:missionId" element={<Mission />} />
                <Route path="/mission/:missionId/confirmation" element={<MissionConfirmation />} />

                {/* Profile/Identity pages */}
                <Route path="/identity-verification" element={<IdentityVerification />} />
                <Route path="/verification-status" element={<VerificationStatus />} />
                <Route path="/hero-status" element={<HeroStatusPage />} />

                {/* Other app pages */}
                <Route path="/explore" element={<Explore />} />
                <Route path="/messages" element={<Messages />} />
              </Route>

              {/* ============ ADMIN ROUTES ============ */}
              <Route path="/admin" element={<Admin />} />
              
              {/* ============ DEV/DEBUG ROUTES ============ */}
              <Route path="/design" element={<Navigate to="/" replace />} />
              <Route path="/design/badges" element={<Navigate to="/" replace />} />
              <Route path="/trust-score" element={<TrustScoreShowcase />} />
              {/* /check-in route removed from production */}

              {/* ============ FALLBACK ============ */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            
            <FlitshipToaster />
            <CookieBanner />
          </BrowserRouter>
          </CoachMarkProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
